import { inject } from '@angular/core'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import { catchError, map, of, switchMap, tap, withLatestFrom } from 'rxjs'

import { FlxModalService } from '@flowx/angular-ui-toolkit'
import { CopyPasteEntityConfirmationModalComponent } from '@flowx/designer/modules/data-model-builder/components/copy-paste-entity-confirmation-modal/copy-paste-entity-confirmation-modal.component'
import { ProcessDataModelApiService } from '@flowx/designer/shared/api/process-data-model.api'
import {
  ApplicationResourceType,
  mapResource,
} from '@flowx/designer/shared/entities/application.entity'
import { ClipboardService } from '@flowx/designer/shared/services/clipboard.service'
import { selectCurrentResourceId } from '@flowx/designer/shared/store/selectors/data-model.selectors'
import { isHttpConflictStatusCode } from '@flowx/shared/utils'

import { createDataModelEffects } from './data-model.effects'

import { IError } from '../../api/api-http.service'
import { ProcessDataModelEntityToUIEntity } from '../../entities/data-model.entity'
import { ToastMessageService } from '../../services/toast-message.service'
import { ProcessDataModelActions } from '../actions/process-data-model.actions'

const processDataModelBasicEffects = createDataModelEffects(
  ApplicationResourceType.Process,
  {
    load: ProcessDataModelActions.loadEntities,
    loadSuccess: ProcessDataModelActions.loadEntitiesSuccess,
    loadFailure: ProcessDataModelActions.loadEntitiesFailure,
    add: ProcessDataModelActions.addEntity,
    addSuccess: ProcessDataModelActions.addEntitySuccess,
    addFailure: ProcessDataModelActions.addEntityFailure,
    edit: ProcessDataModelActions.editEntity,
    editSuccess: ProcessDataModelActions.editEntitySuccess,
    editFailure: ProcessDataModelActions.editEntityFailure,
    addAttribute: ProcessDataModelActions.addAttribute,
    addAttributeSuccess: ProcessDataModelActions.addAttributeSuccess,
    addAttributeFailure: ProcessDataModelActions.addAttributeFailure,
    editAttribute: ProcessDataModelActions.editAttribute,
    editAttributeSuccess: ProcessDataModelActions.editAttributeSuccess,
    editAttributeFailure: ProcessDataModelActions.editAttributeFailure,
    requestDeleteEntity: ProcessDataModelActions.deleteEntityRequested,
    deleteEntity: ProcessDataModelActions.deleteEntity,
    deleteEntitySuccess: ProcessDataModelActions.deleteEntitySuccess,
    deleteEntityFailure: ProcessDataModelActions.deleteEntityFailure,
    requestDeleteAttribute: ProcessDataModelActions.deleteAttributeRequested,
    deleteAttribute: ProcessDataModelActions.deleteAttribute,
    deleteAttributeSuccess: ProcessDataModelActions.deleteAttributeSuccess,
    deleteAttributeFailure: ProcessDataModelActions.deleteAttributeFailure,
    resetErrors: ProcessDataModelActions.resetErrors,
    resetErrorsSuccess: ProcessDataModelActions.resetErrorsSuccess,
    updateAttributeValidators: ProcessDataModelActions.updateAttributeValidators,
    updateAttributeValidatorsSuccess: ProcessDataModelActions.updateAttributeValidatorsSuccess,
    updateAttributeValidatorsFailure: ProcessDataModelActions.updateAttributeValidatorsFailure,
  },
  ProcessDataModelApiService
)

export const processLoadEntities$ = processDataModelBasicEffects.loadEntities$
export const processAddEntity$ = processDataModelBasicEffects.addEntity$
export const processEditEntity$ = processDataModelBasicEffects.editEntity$
export const processAddAttribute$ = processDataModelBasicEffects.addAttribute$
export const processEditAttribute$ = processDataModelBasicEffects.editAttribute$
export const processSetNewEntityAsAttribute$ = processDataModelBasicEffects.setNewEntityAsAttribute$
export const processStartDeleteEntity$ = processDataModelBasicEffects.startDeleteEntity$
export const processDeleteEntity$ = processDataModelBasicEffects.deleteEntity$
export const processStartDeleteAttribute$ = processDataModelBasicEffects.startDeleteAttribute$
export const processDeleteAttribute$ = processDataModelBasicEffects.deleteAttribute$
export const processResetErrors$ = processDataModelBasicEffects.resetErrors$
export const processUpdateAttributeValidators$ =
  processDataModelBasicEffects.updateAttributeValidators$

export const bulkUpdateAttributes$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    dataModelBuilderApiService = inject(ProcessDataModelApiService),
    toastMessageService = inject(ToastMessageService)
  ) =>
    actions$.pipe(
      ofType(ProcessDataModelActions.bulkUpdateAttributes),
      withLatestFrom(store.select(selectCurrentResourceId)),
      switchMap(([{ entityId, attributeUpdates }, resourceId]) =>
        dataModelBuilderApiService
          .bulkUpdateAttributesInDataModelEntity(resourceId, entityId, attributeUpdates)
          .pipe(
            map((entity) => mapResource(entity)),
            map((attributes) =>
              ProcessDataModelActions.bulkUpdateAttributesSuccess({
                resourceId,
                entityId,
                attributes,
              })
            ),
            catchError(({ error }: { error: IError }) => {
              toastMessageService.showErrorMessage(
                'Bulk update data model attributes failed',
                error.title
              )
              return of(
                ProcessDataModelActions.bulkUpdateAttributesFailure({
                  resourceId,
                  entityId,
                })
              )
            })
          )
      )
    ),
  {
    functional: true,
  }
)

export const startCopyEntity$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    dataModelBuilderApiService = inject(ProcessDataModelApiService),
    toastMessageService = inject(ToastMessageService)
  ) =>
    actions$.pipe(
      ofType(ProcessDataModelActions.copyEntityRequested),
      withLatestFrom(store.select(selectCurrentResourceId)),
      switchMap(([{ entityId }, resourceId]) =>
        dataModelBuilderApiService.copyEntities(resourceId, [entityId]).pipe(
          map((entity) => mapResource(entity)),
          map((resp) =>
            ProcessDataModelActions.copyEntitiesToClipboard({
              resourceId,
              copiedEntities: resp,
            })
          ),
          catchError(({ error }: { error: IError }) => {
            toastMessageService.showErrorMessage('Copy data model type failed', error.title)
            return of(ProcessDataModelActions.copyEntitiesToClipboardFailure())
          })
        )
      )
    ),
  {
    functional: true,
  }
)

export const copyEntitiesToClipboard$ = createEffect(
  (
    actions$ = inject(Actions),
    toastMessageService = inject(ToastMessageService),
    clipboardService = inject(ClipboardService)
  ) =>
    actions$.pipe(
      ofType(ProcessDataModelActions.copyEntitiesToClipboard),
      switchMap(({ copiedEntities }) => {
        let copiedEntitiesStringified = null
        try {
          copiedEntitiesStringified = JSON.stringify(copiedEntities)
        } catch (error) {
          toastMessageService.showErrorMessage('Error copying entities!')
          return of(ProcessDataModelActions.copyEntitiesToClipboardFailure())
        }
        return clipboardService.copyTo('RESOURCE_DATA_MODEL', copiedEntitiesStringified).pipe(
          tap(() => toastMessageService.showSuccessMessage('Entities copied to clipboard')),
          map(() => ProcessDataModelActions.copyEntitiesToClipboardSuccess())
        )
      })
    ),
  {
    functional: true,
  }
)

export const startPasteEntities$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    toastMessageService = inject(ToastMessageService),
    clipboardService = inject(ClipboardService)
  ) =>
    actions$.pipe(
      ofType(ProcessDataModelActions.pasteEntitiesRequested),
      withLatestFrom(store.select(selectCurrentResourceId)),
      switchMap(([, resourceId]) =>
        clipboardService.pasteFrom('RESOURCE_DATA_MODEL').pipe(
          map((pastedEntitiesStringified) => {
            try {
              const pastedEntities = JSON.parse(pastedEntitiesStringified)
              return ProcessDataModelActions.pasteEntities({
                resourceId,
                pastedEntities,
                force: false,
              })
            } catch (error) {
              toastMessageService.showErrorMessage('Error pasting entities!')
              return ProcessDataModelActions.pasteEntitiesFailure()
            }
          })
        )
      )
    ),
  {
    functional: true,
  }
)

export const pasteEntities$ = createEffect(
  (
    actions$ = inject(Actions),
    dataModelBuilderApiService = inject(ProcessDataModelApiService),
    flxModalService = inject(FlxModalService),
    toastMessageService = inject(ToastMessageService)
  ) =>
    actions$.pipe(
      ofType(ProcessDataModelActions.pasteEntities),
      switchMap(({ resourceId, pastedEntities, force }) =>
        dataModelBuilderApiService.pasteEntities(resourceId, pastedEntities, force).pipe(
          map((entities) => mapResource(entities)),
          map((entities) =>
            ProcessDataModelActions.pasteEntitiesSuccess({
              resourceId,
              pastedEntities: entities.map((e) => ProcessDataModelEntityToUIEntity(e)),
            })
          ),
          tap(() => toastMessageService.showSuccessMessage('Entities pasted from clipboard')),
          tap(() => flxModalService.hide()),
          catchError((error) => {
            if (isHttpConflictStatusCode(error?.status)) {
              return flxModalService
                .show(
                  CopyPasteEntityConfirmationModalComponent,
                  { closeOnBackdropClick: true, backdropClass: 'overlay-backdrop' },
                  {
                    sourceEntities: pastedEntities.dataEntities.map((entity) => entity.name),
                    conflictedEntities: error.error.pastedEntityNamesThatClash,
                  }
                )
                .data$.pipe(
                  map(() =>
                    ProcessDataModelActions.pasteEntities({
                      resourceId,
                      pastedEntities,
                      force: true,
                    })
                  )
                )
            } else {
              toastMessageService.showErrorMessage('Paste entities failed')
              return of(ProcessDataModelActions.pasteEntitiesFailure())
            }
          })
        )
      )
    ),
  {
    functional: true,
  }
)

export const autoLink$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    dataModelBuilderApiService = inject(ProcessDataModelApiService),
    toastMessageService = inject(ToastMessageService)
  ) =>
    actions$.pipe(
      ofType(ProcessDataModelActions.autoLinkRequested),
      withLatestFrom(store.select(selectCurrentResourceId)),
      switchMap(([, resourceId]) =>
        dataModelBuilderApiService.autoLinkAttributes(resourceId).pipe(
          tap(() =>
            toastMessageService.showSuccessMessage(
              'The references between the data model and the paths specified in the UI elements have been established.'
            )
          ),
          map(() => ProcessDataModelActions.autoLinkSuccess({ resourceId })),
          catchError(() => {
            toastMessageService.showErrorMessage(
              'There was an error creating the references between existing data model and paths filled in UI Elements.'
            )
            return of(ProcessDataModelActions.autoLinkFailure({ resourceId }))
          })
        )
      )
    ),
  { functional: true }
)
